import { useState, useRef, useEffect } from 'react';
import { cn, formatRelativeTime, getInitials } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Send,
  Paperclip,
  MoreVertical,
  ArrowLeft,
  Phone,
  Bot,
  User,
  ShoppingCart,
  FileText,
  Package,
} from 'lucide-react';

export function ChatView({ conversation, messages, onSendMessage, onBack }) {
  const [newMessage, setNewMessage] = useState('');
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const handleSend = () => {
    if (newMessage.trim()) {
      onSendMessage(newMessage);
      setNewMessage('');
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  if (!conversation) {
    return (
      <div className="h-full flex items-center justify-center bg-muted/20">
        <div className="text-center">
          <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
            <MessageSquareIcon className="w-8 h-8 text-muted-foreground" />
          </div>
          <h3 className="text-lg font-medium mb-2">Select a conversation</h3>
          <p className="text-muted-foreground text-sm">
            Choose a conversation from the list to start chatting
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-background">
      {/* Chat Header */}
      <div className="h-16 px-4 flex items-center justify-between border-b border-border bg-card">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" className="md:hidden" onClick={onBack}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <Avatar>
            <AvatarFallback className="bg-primary/10 text-primary">
              {getInitials(conversation.customer.name)}
            </AvatarFallback>
          </Avatar>
          <div>
            <h3 className="font-semibold">{conversation.customer.name}</h3>
            <p className="text-xs text-muted-foreground">{conversation.customer.phone}</p>
          </div>
        </div>

        <div className="flex items-center gap-1 sm:gap-2">
          <Badge
            variant={conversation.mode === 'ai' ? 'default' : 'secondary'}
            className="hidden sm:inline-flex"
          >
            {conversation.mode === 'ai' ? (
              <><Bot className="w-3 h-3 mr-1" /> AI Mode</>
            ) : (
              <><User className="w-3 h-3 mr-1" /> Manual</>
            )}
          </Badge>
          
          <Button variant="ghost" size="icon">
            <Phone className="h-4 w-4" />
          </Button>
          
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon">
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem>
                <ShoppingCart className="h-4 w-4 mr-2" />
                Create Order
              </DropdownMenuItem>
              <DropdownMenuItem>
                <Package className="h-4 w-4 mr-2" />
                Send Product
              </DropdownMenuItem>
              <DropdownMenuItem>
                <FileText className="h-4 w-4 mr-2" />
                Send Invoice
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Messages */}
      <ScrollArea className="flex-1 p-4" ref={scrollRef}>
        <div className="space-y-4">
          {messages.map((message) => (
            <div
              key={message.id}
              className={cn(
                'flex',
                message.sender === 'customer' ? 'justify-start' : 'justify-end'
              )}
            >
              <div
                className={cn(
                  'max-w-[85%] sm:max-w-[70%] rounded-2xl px-4 py-2',
                  message.sender === 'customer'
                    ? 'bg-muted text-foreground rounded-bl-md'
                    : message.sender === 'ai'
                    ? 'bg-blue-500 text-white rounded-br-md'
                    : 'bg-primary text-primary-foreground rounded-br-md'
                )}
              >
                <p className="text-sm">{message.content}</p>
                <p
                  className={cn(
                    'text-[10px] mt-1',
                    message.sender === 'customer'
                      ? 'text-muted-foreground'
                      : 'text-white/70'
                  )}
                >
                  {formatRelativeTime(message.timestamp)}
                  {message.sender === 'ai' && ' · AI'}
                </p>
              </div>
            </div>
          ))}
        </div>
      </ScrollArea>

      {/* Message Input */}
      <div className="p-4 border-t border-border bg-card">
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon">
            <Paperclip className="h-5 w-5" />
          </Button>
          <Input
            placeholder="Type a message..."
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyDown={handleKeyDown}
            className="flex-1"
          />
          <Button onClick={handleSend} disabled={!newMessage.trim()}>
            <Send className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}

function MessageSquareIcon(props) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </svg>
  );
}
